import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Users } from "lucide-react"

const COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
]

interface CustomersPerCouncilChartProps {
  data: Array<{
    name: string
    customers: number
    devices: number
  }>
  viewType?: "bar" | "pie"
}

export function CustomersPerCouncilChart({
  data,
  viewType = "bar",
}: CustomersPerCouncilChartProps) {
  const totalCustomers = data.reduce((sum, item) => sum + item.customers, 0)

  if (viewType === "pie") {
    const pieData = data.map((item, idx) => ({
      name: item.name,
      value: item.customers,
      index: idx,
      color: COLORS[idx % COLORS.length],
    }))
    const visiblePieData = pieData.filter((item) => item.value > 0)

    return (
      <Card className="h-full">
        <CardHeader className="text-center">
          <CardTitle className="text-lg flex items-center gap-2 justify-center">
            <Users className="size-5" />
            End User Distribution
          </CardTitle>
          <p className="text-sm text-muted-foreground">Total end users across all customers</p>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={visiblePieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={0}
                  dataKey="value"
                  nameKey="name"
                >
                  {visiblePieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const entry = payload[0]?.payload
                      if (!entry) return null
                      return (
                        <div className="bg-card border rounded-lg p-3 shadow-lg">
                          <p className="font-medium">{entry.name}</p>
                          <p style={{ color: entry.color }}>
                            {entry.value.toLocaleString()} end users
                          </p>
                        </div>
                      )
                    }
                    return null
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 space-y-2">
            {data.map((item, index) => (
              <div key={item.name} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="size-3 rounded-full"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-sm font-medium">{item.name}</span>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">{item.customers.toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">
                    {((item.customers / totalCustomers) * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="size-5" />
              End Users per Customer
            </CardTitle>
            <p className="text-sm text-muted-foreground">End user distribution across customers</p>
          </div>
          <Badge variant="secondary">Total: {totalCustomers.toLocaleString()}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis dataKey="name" fontSize={12} tick={{ fill: "hsl(var(--muted-foreground))" }} />
              <YAxis
                fontSize={12}
                tick={{ fill: "hsl(var(--muted-foreground))" }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0]?.payload
                    return (
                      <div className="bg-card border rounded-lg p-3 shadow-lg">
                        <p className="font-medium">{label}</p>
                        <p className="text-primary">End users: {data.customers.toLocaleString()}</p>
                        <p className="text-chart-2">Devices: {data.devices.toLocaleString()}</p>
                        <p className="text-xs text-muted-foreground">
                          Ratio: {(data.devices / data.customers).toFixed(2)} devices/end user
                        </p>
                      </div>
                    )
                  }
                  return null
                }}
              />
              <Bar dataKey="customers" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-4 pt-4 border-t">
          {data.map((item, _index) => (
            <div key={item.name} className="text-center">
              <p className="text-xs text-muted-foreground">{item.name}</p>
              <p className="font-semibold">{item.customers.toLocaleString()}</p>
              <p className="text-xs text-chart-2">{item.devices} devices</p>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
